import {
  faAngleDown,
  faFile,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { Breadcrumbs, Button, Dropdown, Icon } from "@intility/bifrost-react";
import Tabs from "@intility/bifrost-react/Tabs";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import illustrationDark from "../../assets/myhub-illustration-dark.svg";
import illustrationLight from "../../assets/myhub-illustration-light.svg";
import MyClients from "./MyClients";
import s from "./MyHub.module.css";
import MyPlatforms from "./MyPlatforms";

const bandStyle = {
  backgroundColor: "var(--bfc-base-2)",
  borderBottom: "1px solid var(--bfc-base-c-dimmed)",
};

// No "New blueprint": a blueprint is created with its platform.
type CreateAction = "platform" | "client";

const CREATE_ACTIONS: Record<CreateAction, { label: string; to: string }> = {
  platform: { label: "New platform", to: "/platforms/new" },
  client: { label: "New client", to: "/clients/new" },
};

const ACTION_KEYS = Object.keys(CREATE_ACTIONS) as CreateAction[];

export default function MyHub() {
  const navigate = useNavigate();
  const [action, setAction] = useState<CreateAction>("platform");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"platforms" | "clients">("platforms");

  return (
    <div className="bf-content">
      {/* Full-width: content fills the nav area, with Bifrost page padding at each end. */}
      <header className={`bf-page-padding ${s.header}`}>
        <Breadcrumbs>
          <Breadcrumbs.Item>
            <Link to="/">Home</Link>
          </Breadcrumbs.Item>
          <Breadcrumbs.Item>My Hub</Breadcrumbs.Item>
        </Breadcrumbs>
        <h1 className="bf-h1" style={{ margin: 0, marginTop: 12 }}>
          My Hub
        </h1>
        <p className="bf-p" style={{ marginTop: 8, marginBottom: 0 }}>
          Platforms and clients you own and maintain
        </p>
        <img
          src={illustrationLight}
          alt=""
          className={`${s.illustration} ${s.illustrationLight}`}
        />
        <img
          src={illustrationDark}
          alt=""
          className={`${s.illustration} ${s.illustrationDark}`}
        />
      </header>

      <div
        className="bf-page-padding-x"
        style={{
          ...bandStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          paddingBlock: 12,
        }}
      >
        <Link className="bf-button bf-button-flat" to="/docs/concepts">
          <Icon icon={faFile} style={{ marginRight: 8 }} />
          Documentation
        </Link>
        <div className={s.split}>
          <Button
            variant="filled"
            className={s.splitMain}
            onClick={() => navigate(CREATE_ACTIONS[action].to)}
          >
            <Icon icon={faPlus} style={{ marginRight: 8 }} />
            <span className={s.labels}>
              {ACTION_KEYS.map((key) => (
                <span
                  key={key}
                  className={`${s.label} ${key === action ? "" : s.labelHidden}`}
                >
                  {CREATE_ACTIONS[key].label}
                </span>
              ))}
            </span>
          </Button>
          <Dropdown
            visible={open}
            onHide={() => setOpen(false)}
            placement="bottom-end"
            content={ACTION_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setAction(key);
                  setOpen(false);
                }}
              >
                <Dropdown.Item>{CREATE_ACTIONS[key].label}</Dropdown.Item>
              </button>
            ))}
          >
            <Button
              variant="filled"
              className={s.splitChevron}
              aria-label="Choose what to create"
              onClick={() => setOpen((v) => !v)}
            >
              <Icon icon={faAngleDown} />
            </Button>
          </Dropdown>
        </div>
      </div>

      <div style={{ backgroundColor: "var(--bfc-base-2)" }}>
        <Tabs variant="styled" contentBackground="base" noBackground>
          <Tabs.Item
            active={tab === "platforms"}
            onClick={() => setTab("platforms")}
            content={<MyPlatforms />}
          >
            My platforms
          </Tabs.Item>
          <Tabs.Item
            active={tab === "clients"}
            onClick={() => setTab("clients")}
            content={<MyClients />}
          >
            My clients
          </Tabs.Item>
        </Tabs>
      </div>
    </div>
  );
}
